
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import {
  textAndRichTextVariableItem,
  Control,
  ControlConditions,
  textAndRichTextVariableOptions
} from "@/types/controls";
import { ElementStudioContext } from "./ElementStudioTabsAndPanels.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import ElementStudioConditionInput from "@/components/ElementStudio/ElementStudioConditionInput.vue";
import defensiveCopy from "@/util/defensive-copy";
import { getElementControlByElementControlPathWithPrefix } from "@/lib/elements/utils";
import Icon from "@/components/UI/Icon.vue";
import ElementStudioIconsAutocomplete from "@/components/ElementStudio/ElementStudioIconsAutocomplete.vue";
import NiceButton from "@/components/UI/NiceButton.vue";

const DEFAULT_POPULATE = {
  path: "",
  text: "",
  value: "",
  condition: undefined
};

@Component({
  components: {
    NiceButton,
    ElementStudioIconsAutocomplete,
    Icon,
    ElementStudioConditionInput,
    Repeater,
    TextInput
  }
})
export default class ElementStudioVariableOptions extends Vue {
  @Prop()
  value!: textAndRichTextVariableOptions;

  @Prop()
  control!: Control;

  @Prop()
  path!: string;

  @Inject("context") readonly context!: ElementStudioContext;

  enabled = false;

  get internalValue() {
    return this.value || {};
  }

  get paths() {
    return this.context.getPathsToAllControls(["repeater"]);
  }

  get possibleValues() {
    const path = this.internalValue?.populate?.path;

    if (!path) return [];

    const control = this.getControl(path);

    if (!control) return [];

    const result = control.children.map(c => c.slug);

    if (!result) return [];

    return result;
  }

  getControl(path: string) {
    return getElementControlByElementControlPathWithPrefix(this.element, path);
  }

  get populate() {
    return this.internalValue.populate || DEFAULT_POPULATE;
  }

  get variables() {
    return (
      this.control.options.variableItems || [{ value: "value", text: "Label" }]
    );
  }

  get element() {
    return this.context.element();
  }

  updateVariableItems(newItems: textAndRichTextVariableItem[]) {
    this.$emit("updateVariableItems", newItems);
  }

  updateVariableItem(newItem: textAndRichTextVariableItem, index: number) {
    this.$emit("updateVariableItem", { newItem, index });
  }

  updateEnablePopulate(enabled: boolean) {
    if (enabled) {
      this.emit({
        populate: DEFAULT_POPULATE
      });
    } else {
      this.emit({
        populate: undefined
      });
    }
  }

  updateCondition(newCondition: ControlConditions) {
    const newPopulate = defensiveCopy(this.populate);

    if (Array.isArray(newCondition) && !newCondition.length) {
      delete newPopulate.condition;
    } else {
      newPopulate.condition = newCondition;
    }

    this.emit({
      populate: newPopulate
    });
  }

  updatePopulateValue(key: "path" | "text" | "value", value: string) {
    const newPopulate = defensiveCopy(this.populate);

    newPopulate[key] = value;

    this.emit({
      populate: newPopulate
    });
  }

  toggleEnabled(value: boolean) {
    this.emit({
      enabled: value
    });
  }

  emit(variableOptions: textAndRichTextVariableOptions | undefined) {
    this.$emit("input", {
      ...this.internalValue,
      ...variableOptions
    });
  }
}
